/**
 * PLP Hero Component
 *
 * Renders the visual hero section for Product Listing Pages.
 * Supports background images/videos, headings, subheaders, and result counts.
 *
 * Styling respects Contentful settings:
 * - Heading tag (h1, h2, etc.) via headingSeoTag
 * - Text color via textColor
 * - Background color and opacity
 * - Text alignment
 * - Subheading capitalization via subheaderIsCapitalized
 * - Font: Uses Italian Plate (--font-display) for headings
 *
 * Note: Breadcrumbs and filter bar are handled separately by the parent page.
 * This component focuses purely on the visual hero presentation.
 */

import { cdn } from '@/lib/cdn';
import type { HeroSettings } from '@/lib/listingData';
import Image from 'next/image';
import { ExpandableText } from './ExpandableText';
import styles from './PlpHero.module.scss';

// Character limit for subtitle before showing "See more" toggle
const SUBTITLE_MAX_LENGTH = 200;

// ============================================
// Types
// ============================================

/**
 * Props for PLPHero component using unified HeroSettings
 */
interface PLPHeroProps {
  /** Hero settings from listingData orchestrator */
  settings: HeroSettings;
  /** Optional className for custom styling */
  className?: string;
}

// ============================================
// Component
// ============================================

/**
 * PLP Hero - Visual hero section for listing pages
 *
 * Renders heading, subheading, background media, and product count.
 * Does NOT render breadcrumbs or filter bar (parent handles those).
 */
export function PLPHero({ settings, className }: PLPHeroProps) {
  const {
    heading,
    subheading,
    textAlignment = 'center',
    headingNumberOfColoredWords,
    headingSeoTag,
    subheaderIsCapitalized,
    media,
    backgroundColor,
    backgroundColorOpacity,
    textColor,
    textBelowMedia,
  } = settings;

  // Skip rendering if no heading and no media
  if (!heading && !media) {
    return null;
  }

  // Determine heading element type from CMS
  const HeadingTag = (headingSeoTag as keyof JSX.IntrinsicElements) || 'h1';

  // Build text style object for custom colors
  const textStyle: React.CSSProperties = {
    ...(textColor && { color: textColor }),
  };

  // Check if heading contains HTML (e.g., <br> tags)
  const headingHasHtml = heading && /<[^>]+>/.test(heading);

  // Apply colored words styling to heading
  const renderHeading = () => {
    if (!heading) return null;

    // If heading contains HTML tags (like <br>), render with dangerouslySetInnerHTML
    if (headingHasHtml) {
      return (
        <HeadingTag
          className={styles.heading}
          style={textStyle}
          dangerouslySetInnerHTML={{ __html: heading }}
        />
      );
    }

    if (headingNumberOfColoredWords && headingNumberOfColoredWords > 0) {
      const words = heading.split(' ');
      const coloredWords = words.slice(0, headingNumberOfColoredWords).join(' ');
      const remainingWords = words.slice(headingNumberOfColoredWords).join(' ');

      return (
        <HeadingTag className={styles.heading} style={textStyle}>
          <span className={styles.coloredText}>{coloredWords}</span>
          {remainingWords && ` ${remainingWords}`}
        </HeadingTag>
      );
    }

    return (
      <HeadingTag className={styles.heading} style={textStyle}>
        {heading}
      </HeadingTag>
    );
  };

  // Content section (heading + subheader)
  // Note: Item count is NOT shown here - it's displayed in the filter bar below
  const renderContent = () => (
    <div className={styles.content} style={{ textAlign: textAlignment }}>
      {renderHeading()}
      {subheading && (
        <ExpandableText
          text={subheading}
          maxLength={SUBTITLE_MAX_LENGTH}
          className={`${styles.subheader} ${subheaderIsCapitalized ? styles.capitalized : ''}`}
          style={textStyle}
        />
      )}
    </div>
  );

  // Render background media (image or video)
  const renderMedia = () => {
    if (!media) return null;

    if (media.type === 'video') {
      return (
        <video className={styles.backgroundVideo} autoPlay muted loop playsInline>
          <source src={cdn.transformVideoUrl(media.url)} type="video/mp4" />
        </video>
      );
    }

    return (
      <Image
        src={media.url}
        alt={media.alt || ''}
        fill
        className={styles.backgroundImage}
        priority
        style={
          media.focalPoint
            ? {
                objectPosition: `${media.focalPoint.x * 100}% ${media.focalPoint.y * 100}%`,
              }
            : undefined
        }
      />
    );
  };

  // Build background container style
  const buildBackgroundStyle = (): React.CSSProperties => ({
    ...(backgroundColor && { backgroundColor }),
  });

  // Build overlay style (handles opacity)
  const buildOverlayStyle = (): React.CSSProperties | undefined => {
    if (backgroundColorOpacity == null) return undefined;
    return { opacity: backgroundColorOpacity };
  };

  return (
    <section className={`${styles.hero} ${className || ''}`}>
      {/* Hero content - either over image or below it */}
      {!textBelowMedia ? (
        // Text over image layout
        <div className={styles.heroBackground} style={buildBackgroundStyle()}>
          {renderMedia()}
          {media && <div className={styles.overlay} style={buildOverlayStyle()} />}
          {renderContent()}
        </div>
      ) : (
        // Image then text below layout
        <>
          {media && <div className={styles.mediaContainer}>{renderMedia()}</div>}
          <div className={styles.textBelowContainer} style={buildBackgroundStyle()}>
            {renderContent()}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Simple title-only hero (no background)
 * Used when there's no HeroProductListing but we still want a title
 *
 * Styling respects Contentful settings when provided via settings prop,
 * otherwise uses sensible defaults.
 */
export function PLPHeroSimple({
  title,
  subtitle,
  className,
  textAlignment = 'center',
  headingSeoTag = 'h1',
  subheaderIsCapitalized = false,
  textColor,
  backgroundColor,
}: {
  title: string;
  subtitle?: string;
  className?: string;
  textAlignment?: 'left' | 'center' | 'right';
  headingSeoTag?: string;
  subheaderIsCapitalized?: boolean;
  textColor?: string | null;
  backgroundColor?: string | null;
}) {
  // Determine heading element type from CMS
  const HeadingTag = (headingSeoTag as keyof JSX.IntrinsicElements) || 'h1';

  // Build text style object for custom colors
  const textStyle: React.CSSProperties = {
    ...(textColor && { color: textColor }),
  };

  return (
    <section
      className={`${styles.hero} ${className || ''}`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className={styles.simpleContent} style={{ textAlign: textAlignment }}>
        <HeadingTag className={styles.heading} style={textStyle}>
          {title}
        </HeadingTag>
        {subtitle && (
          <p
            className={`${styles.subheader} ${subheaderIsCapitalized ? styles.capitalized : ''}`}
            style={textStyle}
          >
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
}

PLPHero.displayName = 'PLPHero';
PLPHeroSimple.displayName = 'PLPHeroSimple';
